'use client'

import { useT } from '@/i18n/client'
import { InboxOutlined } from '@ant-design/icons'
import { Form, FormItemProps, Space, Typography, Upload } from 'antd'
import { UploadFile } from 'antd/es/upload'
import { ReactNode, useCallback } from 'react'
import { doesntExceedSize } from '../rules'
import {
  AttachmentType,
  attachmentTypes as attachmentTypesBase,
  FileType,
  formatBytes,
} from './AttachmentFile'

const { Dragger } = Upload
const { Text } = Typography

interface Props {
  label?: ReactNode
  hideCloseIcon?: boolean
  formItem: FormItemProps
  dragger?: {
    multiple?: boolean
    fileTypes?: FileType[]
    title?: string
    subtitle?: string
  }
}

const AttachmentEditor = ({
  label = 'Attachment',
  formItem = {
    name: 'file',
    rules: [],
  },
  dragger = {
    multiple: false,
    fileTypes: undefined,
  },
}: Props) => {
  const { t } = useT('form')
  const attachmentTypes = useCallback((): AttachmentType[] => {
    const types =
      dragger.fileTypes ?? (Object.keys(attachmentTypesBase) as FileType[])
    return types.map((t) => attachmentTypesBase[t])
  }, [dragger.fileTypes])

  const form = Form.useFormInstance()

  const validateFiles = (fileList: UploadFile[]) => {
    return fileList.map((file) => {
      const attachmentType = attachmentTypes().find((at) =>
        at.accept.includes(file.type as FileType)
      )

      if (
        !attachmentType ||
        (file.size && file.size > attachmentType.maxSize)
      ) {
        return { ...file, status: 'error' }
      }
      return file
    })
  }

  return (
    <Space vertical className="w-full">
      <Form.Item
        label={label}
        name={formItem.name}
        rules={[
          ...(formItem.rules ?? []),
          doesntExceedSize(attachmentTypes(), t),
        ]}
        valuePropName="fileList"
        getValueFromEvent={(e: any) => (Array.isArray(e) ? e : e?.fileList)}
      >
        <Dragger
          maxCount={dragger.multiple ? undefined : 1}
          name={formItem.name}
          multiple={dragger.multiple}
          listType={'picture'}
          accept={attachmentTypes()
            .map((at) => at.accept.join(','))
            .flat()
            .join(',')}
          beforeUpload={() => false}
          onChange={({ fileList }) => {
            const validatedFiles = validateFiles(fileList)
            form.setFieldValue(formItem.name, validatedFiles)
          }}
        >
          <Space vertical>
            <Text style={{ fontSize: 30 }}>
              <InboxOutlined />
            </Text>
            {dragger.title && (
              <Text style={{ fontWeight: 'bold' }}>
                {dragger.title ?? 'FileClicOrDrag'}
              </Text>
            )}
            {dragger.subtitle && <Text italic>{dragger.subtitle}</Text>}
            <Space vertical size={2}>
              {attachmentTypes().map((type, index) => (
                <Text key={index}>
                  {type.name} - {type.extensions.join(', ')} - Max.
                  {formatBytes(type.maxSize)}
                </Text>
              ))}
            </Space>
          </Space>
        </Dragger>
      </Form.Item>
    </Space>
  )
}

export default AttachmentEditor
